import { memo, useCallback, useMemo } from 'react';
import { Box, makeStyles } from '@material-ui/core';
import {
  useSortable,
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent
} from '@dnd-kit/core';
import { SText } from '@setvi/shared/components';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { DragHandleIcon } from '@setvi/shared/components/sicons';
import { DraggableItem } from './draggable-item';

const useStyles = makeStyles(() => ({
  productContainer: {
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: '#F8F9FA',
    borderRadius: '16px',
    overflow: 'hidden'
  },
  productHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    padding: '0 12px',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '32px',
    height: '40px',
    color: '#5D7083',
    cursor: 'grab',
    '&:active': {
      cursor: 'grabbing'
    }
  },
  headerContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    minWidth: 0,
    padding: '8px 0'
  },
  title: {
    fontSize: '16px',
    fontWeight: 600,
    lineHeight: '24px',
    color: '#333D47',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  childrenContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    padding: '0 12px 12px 12px'
  },
  childItem: {
    marginLeft: '32px'
  },
  isDragging: {
    opacity: 0.5
  },
  dragOverlay: {
    boxShadow: '0 4px 16px rgba(0, 0, 0, 0.15)',
    backgroundColor: '#FFFFFF'
  }
}));

interface ProductChildrenListProps {
  productChildren: QuoteProductDTO[];
  productId: string;
  onChildrenReorder: (productId: string, children: QuoteProductDTO[]) => void;
}

const ProductChildrenList = memo(
  ({
    productChildren,
    productId,
    onChildrenReorder
  }: ProductChildrenListProps) => {
    const classes = useStyles();

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8
        }
      }),
      useSensor(KeyboardSensor, {
        coordinateGetter: sortableKeyboardCoordinates
      })
    );

    const childIds = useMemo(
      () => productChildren.map(child => child.id || child._id),
      [productChildren]
    );

    const handleChildDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (over && active.id !== over.id) {
          const oldIndex = productChildren.findIndex(
            child => (child.id || child._id) === active.id
          );
          const newIndex = productChildren.findIndex(
            child => (child.id || child._id) === over.id
          );

          if (oldIndex === -1 || newIndex === -1) return;

          const newChildren = arrayMove(productChildren, oldIndex, newIndex);
          onChildrenReorder(productId, newChildren);
        }
      },
      [productChildren, productId, onChildrenReorder]
    );

    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={handleChildDragEnd}>
        <SortableContext
          items={childIds}
          strategy={verticalListSortingStrategy}>
          {productChildren.map(child => (
            <Box key={child.id || child._id} className={classes.childItem}>
              <DraggableItem item={child} />
            </Box>
          ))}
        </SortableContext>
      </DndContext>
    );
  }
);

interface DraggableProductItemProps {
  product: QuoteProductDTO;
  onChildrenReorder: (productId: string, children: QuoteProductDTO[]) => void;
  isDragOverlay?: boolean;
}

export const DraggableProductItem = memo(
  ({
    product,
    onChildrenReorder,
    isDragOverlay = false
  }: DraggableProductItemProps) => {
    const classes = useStyles();
    const productId = product.id || product._id;

    const children = useMemo(
      () => (product.children || []) as QuoteProductDTO[],
      [product.children]
    );

    const {
      attributes,
      listeners,
      setNodeRef,
      transform,
      transition,
      isDragging
    } = useSortable({
      id: productId,
      disabled: isDragOverlay
    });

    const style = {
      transform: CSS.Transform.toString(transform),
      transition
    };

    const hasChildren = children.length > 0;

    return (
      <div
        ref={setNodeRef}
        style={style}
        className={`${classes.productContainer} ${isDragging ? classes.isDragging : ''} ${isDragOverlay ? classes.dragOverlay : ''}`}>
        <div className={classes.productHeader} {...attributes} {...listeners}>
          <Box className={classes.dragHandle}>
            <DragHandleIcon style={{ fontSize: '12px' }} />
          </Box>
          <Box className={classes.headerContent}>
            <SText
              title={product.name || 'Unnamed Product'}
              weight="semibold"
              className={classes.title}
              maxLines={1}
            />
          </Box>
        </div>

        {hasChildren && !isDragOverlay && (
          <Box className={classes.childrenContainer}>
            <ProductChildrenList
              productChildren={children}
              productId={productId}
              onChildrenReorder={onChildrenReorder}
            />
          </Box>
        )}
      </div>
    );
  }
);
